import { FlagProvider, IConfig } from '@unleash/proxy-client-react'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { ConfigSelector } from '~shared-components/redux/selectors/config.selector'
import { isServer } from '~shared-components/utils'

import { useFlags } from '../FlagRender/useFlags'
import { Maintenance } from './Maintenance'
import { useMaintenanceBypass } from './useMaintenanceBypass'

/**
 * Unleash attributes every evaluation to an app name, and the `connect` app on
 * the shared instance is the other Brenntag Connect front end, so APAC
 * identifies as its own app.
 */
const APP_NAME = 'connect-apac'

/**
 * LaunchDarkly streamed flag changes; the Unleash frontend API is polled. Turning
 * maintenance on therefore takes up to this long to reach a browser that is
 * already open. This is the SDK default, stated here because the delay is a
 * behaviour change worth seeing at the call site.
 */
const REFRESH_INTERVAL_SECONDS = 30

/**
 * The bypass hook is called here rather than in `MaintenanceGate` so that it
 * stays below the `isServer()` guard: it reads `sessionStorage`, which does not
 * exist during `renderToString`.
 *
 * It is mounted in both states, so the shortcut is listening whether or not the
 * maintenance page is currently showing. That matters because the flag is polled
 * -- someone already on the app when maintenance is switched on can press the
 * chord as the page swaps in, and a listener mounted only alongside
 * `<Maintenance />` would miss it.
 */
const MaintenanceSwitch: React.FC = ({ children }) => {
  const { maintenancePage } = useFlags()
  const bypassed = useMaintenanceBypass()

  return maintenancePage && !bypassed ? <Maintenance /> : <>{children}</>
}

/**
 * Replaces the app with the maintenance page while `maintenance-page` is on, and
 * renders it untouched otherwise.
 *
 * This is the client-side replacement for the nginx/Lua maintenance gate. That
 * gate is deliberately left in place but goes inert once LaunchDarkly is gone,
 * because it evaluated the flag through the LaunchDarkly server SDK and Unleash
 * publishes no Lua SDK. Consequences accepted with the ticket: no 503 status
 * during maintenance, the document API is no longer gated, and the login pages
 * served by authorizeui are not covered.
 *
 * Three cases are handled deliberately, because a kill switch must never be the
 * thing that takes the app down:
 *
 * - Missing configuration renders `children` with no provider at all. The Unleash
 *   client constructor throws on an empty `url` or `clientKey`, and on clientui it
 *   runs inside `renderToString`, so an unset value would turn every request into
 *   a 500. An environment with no Unleash configured simply cannot show the
 *   maintenance page.
 * - On the server it renders `children` too. Effects do not run during
 *   `renderToString`, so the client could never fetch a flag there; mounting it
 *   would construct a throwaway client per request to always answer `false`.
 * - An unreachable Unleash leaves the flag `false` and the app up. The client
 *   emits `ready` only after a *successful* first fetch, which is why nothing here
 *   waits on `flagsReady`: doing so would turn an Unleash outage into an app that
 *   never renders at all.
 *
 * Ctrl+Shift+M dismisses the page for the current browser session, so the team can
 * reach the application during maintenance without flipping the flag for everyone.
 */
export const MaintenanceGate: React.FC = ({ children }) => {
  const clientKey = useSelector(ConfigSelector.unleashClientKey)
  const url = useSelector(ConfigSelector.unleashUrl)

  const config = useMemo<IConfig | undefined>(
    () =>
      url && clientKey
        ? {
            appName: APP_NAME,
            clientKey,
            // Nothing reads Unleash's metrics for this flag.
            disableMetrics: true,
            refreshInterval: REFRESH_INTERVAL_SECONDS,
            url,
          }
        : undefined,
    [clientKey, url],
  )

  if (isServer() || !config) return <>{children}</>

  return (
    <FlagProvider config={config}>
      <MaintenanceSwitch>{children}</MaintenanceSwitch>
    </FlagProvider>
  )
}
